import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSocket } from '../contexts/SocketContext'
import { useChat } from '../contexts/ChatContext'
import { fretesService } from '../services/api'
import PlanosModal from './PlanosModal'
import CheckoutModal from './CheckoutModal'
import ToastNotificacao from './ToastNotificacao'
import './Header.css'

function Header() {
    const [menuOpen, setMenuOpen] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [showPlanosModal, setShowPlanosModal] = useState(false)
    const [checkoutPlano, setCheckoutPlano] = useState(null)
    const [fretesProximos, setFretesProximos] = useState(0)
    const [conversasNaoLidas, setConversasNaoLidas] = useState(0)
    const [toastPagamento, setToastPagamento] = useState({ visivel: false, tipo: 'sucesso' })
    const location = useLocation()
    const navigate = useNavigate()
    const { usuario, isLogado, logout, atualizarUsuario } = useAuth()
    const { socket } = useSocket()
    const { isOpen: isChatOpen } = useChat()
    const dropdownRef = useRef(null)

    const isActive = (path) => location.pathname === path

    // Fechar dropdowns ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Sonar: detectar pagamentos confirmados e mostrar modal apenas uma vez por fatura
    useEffect(() => {
        if (!isLogado || !usuario) return;
        const token = localStorage.getItem('token');
        if (!token) return;

        let tentativas = 0;
        const checarSonar = () => {
            tentativas++;
            fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/pagamentos/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
                if (data.confirmado && data.gateway_id) {
                    // Verifica se já mostramos o toast para esta fatura nesta sessão
                    const jaMostrou = sessionStorage.getItem(`toast_mostrado_${data.gateway_id}`);
                    
                    if (!jaMostrou) {
                        sessionStorage.setItem(`toast_mostrado_${data.gateway_id}`, 'true');
                        const tipo = data.plano === 'motorista' ? 'motorista' 
                            : data.plano === 'anuncios_ilimitados' ? 'premium' 
                            : data.plano === 'anuncio_avulso' ? 'avulso' : 'sucesso';
                        setToastPagamento({ visivel: true, tipo });
                        if (data.usuario) {
                            atualizarUsuario(data.usuario);
                        }
                    }
                } else if (data.status === 'pendente' && tentativas < 6) {
                    setTimeout(checarSonar, 3000);
                }
            })
            .catch(() => {});
        };
        setTimeout(checarSonar, 2000);
    }, [isLogado, usuario]);

    const handleAnunciarClick = (e) => {
        if (!isLogado || !usuario) return;
        
        // Verifica se a assinatura PRO está ativa E dentro da validade
        const assinaturaValida = usuario.assinatura_ilimitada === true && 
                                usuario.expiracao_assinatura && 
                                new Date(usuario.expiracao_assinatura) > new Date();
        
        const creditos = parseInt(usuario.creditos_anuncio) || 0;
        
        if (!assinaturaValida && creditos < 1) {
            e.preventDefault();
            console.log('Sem plano e sem creditos. Abrindo modal...', {assinaturaValida, creditos, expiracao: usuario.expiracao_assinatura});
            setShowPlanosModal(true);
        } else {
            console.log('Possui plano ou creditos. Navegando para criar anuncio...', {assinaturaValida, creditos});
        }
    };

    const handlePlanoSelecionado = async (plano) => {
        try {
            const token = localStorage.getItem('token');
            const resp = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/pagamentos/checkout-anuncio`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ plano })
            });
            const data = await resp.json();
            if (data.url) {
                window.location.href = data.url; // Redireciona na mesma aba
            } else {
                alert('Erro ao gerar pagamento');
            }
        } catch (e) {
            console.error(e);
            alert('Falha na comunicação com gateway de pagamento');
        }
    };

    // Buscar alertas de fretes se for motorista verificado
    useEffect(() => {
        const isMotoristaHabilitado = usuario?.tipos_habilitados?.includes('motorista') || usuario?.tiposHabilitados?.includes('motorista') || usuario?.tipo === 'motorista';
        const isVerificado = usuario?.motorista_verificado === true && 
                             usuario?.expiracao_motorista && 
                             new Date(usuario?.expiracao_motorista) > new Date();

        if (isLogado && isMotoristaHabilitado && isVerificado) {
            const buscarAlertas = async () => {
                try {
                    const response = await fretesService.contadorProximos()
                    setFretesProximos(response.total)
                } catch (error) {
                    console.error('Erro ao buscar alertas:', error)
                }
            }
            buscarAlertas()
            // Polling a cada 60s
            const interval = setInterval(buscarAlertas, 60000)
            return () => clearInterval(interval)
        }
    }, [isLogado, usuario])

    // Buscar mensagens não lidas
    useEffect(() => {
        if (!isLogado) return

        const buscarNaoLidas = async () => {
            try {
                const token = localStorage.getItem('token');
                const resp = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/chat/nao-lidas`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await resp.json();
                if (data.total !== undefined) {
                    setConversasNaoLidas(data.total);
                }
            } catch (e) {
                console.error("Erro ao buscar mensagens não lidas:", e);
            }
        }

        buscarNaoLidas()

        if (!socket) return

        const handleNovaMensagem = () => {
            buscarNaoLidas();
        }

        socket.on('nova_mensagem', handleNovaMensagem);
        window.addEventListener('mensagens_lidas', buscarNaoLidas);

        return () => {
            socket.off('nova_mensagem', handleNovaMensagem);
            window.removeEventListener('mensagens_lidas', buscarNaoLidas);
        }
    }, [isLogado, socket])

    const perfilIncompleto = usuario && !usuario.perfilCompleto
    const isMotoristaPerfil = usuario?.tipos_habilitados?.includes('motorista') || usuario?.tiposHabilitados?.includes('motorista') || usuario?.tipo === 'motorista';
    const isMotoristaVerificado = (usuario?.motorista_verificado === true || usuario?.motoristaVerificado === true) && 
                                   usuario?.expiracao_motorista && 
                                   new Date(usuario?.expiracao_motorista) > new Date();

    // Menu universal adaptado
    const getMenuItems = () => {
        if (!usuario) return []

        const baseMenu = [
            { to: '/perfil', icon: '👤', label: 'Meu Perfil' },
            { to: '/meus-favoritos', icon: '⭐', label: 'Meus Favoritos' },
            { to: '/meus-anuncios', icon: '📋', label: 'Meus Anúncios', badge: 0 },
            { to: '/minhas-conversas', icon: '💬', label: 'Mensagens' }
        ]

        if (!isMotoristaPerfil) {
            baseMenu.push({ to: '/area-motorista', icon: '🚚', label: 'Seja Motorista' })
        }

        return baseMenu
    }

    return (
        <>
        <header className="header">
            <div className="header-container">
                <Link to="/" className="header-logo">
                    <div className="logo-icon">
                        <img src="/logo/agroov-icone-novo.png" alt="Agroov" className="header-logo-img" />
                    </div>
                </Link>

                <nav className="header-nav-desktop">
                    <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Início</Link>
                    <Link to="/anuncios" className={`nav-link ${isActive('/anuncios') ? 'active' : ''}`}>Anúncios</Link>
                    <Link to="/motoristas" className={`nav-link ${isActive('/motoristas') ? 'active' : ''}`}>Motoristas</Link>
                </nav>

                <div className="header-actions">
                    {isLogado && (
                        <Link to="/minhas-conversas" className="header-chat-icon-desktop" title="Minhas Conversas">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4.913 2.658c2.333-.292 4.7-.408 7.087-.408 2.387 0 4.754.116 7.087.408a2.18 2.18 0 0 1 1.913 2.168v10.32a2.18 2.18 0 0 1-1.913 2.168c-1.028.129-2.064.221-3.11.276l-2.756 2.756a.75.75 0 0 1-1.248-.548v-2.277a48.186 48.186 0 0 1-6.98-.276 2.18 2.18 0 0 1-1.913-2.168V4.826a2.18 2.18 0 0 1 1.913-2.168Z" />
                            </svg>
                            {conversasNaoLidas > 0 && (
                                <span className="badge-count">{conversasNaoLidas}</span>
                            )}
                        </Link>
                    )}
                    {isLogado ? (
                        <>
                            {isMotoristaVerificado && fretesProximos > 0 && (
                                <Link to="/fretes-disponiveis" className="frete-alerta" title={`${fretesProximos} fretes próximos`}>
                                    🚛 <span className="badge-count">{fretesProximos}</span>
                                </Link>
                            )}
                            {perfilIncompleto && (
                                <Link to="/completar-perfil" className="perfil-alerta">
                                    ⚠️ <span>Complete seu perfil</span>
                                </Link>
                            )}
                            {usuario?.assinatura_ilimitada && usuario?.expiracao_assinatura && new Date(usuario.expiracao_assinatura) > new Date() && (
                                <div className="pro-countdown has-tooltip">
                                    <span className="pro-icon">⚡</span>
                                    <span className="pro-days">
                                        {Math.max(0, Math.ceil((new Date(usuario.expiracao_assinatura) - new Date()) / (1000 * 60 * 60 * 24)))} dias
                                    </span>
                                    <div className="tooltip-box">
                                        <strong>Plano Produtor PRO</strong>
                                        <p>Seu plano está ativo! Aproveite anúncios ilimitados e destaque máximo.</p>
                                    </div>
                                </div>
                            )}
                            {isMotoristaVerificado && (
                                <div className="pro-countdown motorista-countdown has-tooltip" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.2)' }}>
                                    <span className="pro-icon">🚚</span>
                                    <span className="pro-days" style={{ color: '#3b82f6' }}>
                                        {Math.max(0, Math.ceil((new Date(usuario.expiracao_motorista) - new Date()) / (1000 * 60 * 60 * 24)))} dias
                                    </span>
                                    <div className="tooltip-box" style={{ borderColor: 'rgba(59, 130, 246, 0.3)' }}>
                                        <strong style={{ color: '#3b82f6' }}>Habilitação Transportador</strong>
                                        <p>Sua licença de fretes está ativa! Você pode fechar rotas livremente.</p>
                                    </div>
                                </div>
                            )}
                            <Link 
                                to="/criar-anuncio" 
                                className="btn btn-primary btn-sm btn-novo-anuncio" 
                                style={{ marginRight: '15px' }}
                            >
                                Anunciar
                            </Link>

                            <div className="user-dropdown" ref={dropdownRef}>
                                <button
                                    className="user-trigger"
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    <div className="user-avatar">
                                        {usuario?.fotoPerfil ? (
                                            <img src={usuario.fotoPerfil?.startsWith('http') ? usuario.fotoPerfil : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${usuario.fotoPerfil}`} alt={usuario.nome} className="header-avatar-img" />
                                        ) : (
                                            usuario?.nome?.charAt(0).toUpperCase() || 'U'
                                        )}
                                    </div>
                                    <div className="user-info-header">
                                        <span className="user-name">Olá, {usuario?.nome?.split(' ')[0] || 'Usuário'}</span>
                                        {usuario?.perfilCompleto && (
                                            <span className="verificado-mini">✓ Verificado</span>
                                        )}
                                    </div>
                                    <span className="dropdown-arrow">▼</span>
                                </button>

                                {dropdownOpen && (
                                    <div className="dropdown-menu">
                                        {getMenuItems().map((item) => (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                className="dropdown-item"
                                                onClick={() => setDropdownOpen(false)}
                                            >
                                                {item.icon} {item.label}
                                                {item.badge > 0 && <span className="menu-badge">{item.badge}</span>}
                                            </Link>
                                        ))}
                                        <div className="dropdown-divider"></div>
                                        <button
                                            className="dropdown-item dropdown-logout"
                                            onClick={() => {
                                                setDropdownOpen(false)
                                                logout()
                                            }}
                                        >
                                            🚪 Sair
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="btn btn-secondary btn-sm">Entrar</Link>
                            <Link to="/cadastro" className="btn btn-primary btn-sm">Cadastrar</Link>
                        </>
                    )}
                </div>

                <button
                    className="menu-toggle"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Menu"
                >
                    <span className={`hamburger ${menuOpen ? 'open' : ''}`}></span>
                </button>
            </div>

            <nav className={`header-nav-mobile ${menuOpen ? 'open' : ''}`}>
                <Link to="/" className="nav-link" onClick={() => setMenuOpen(false)}>Início</Link>
                <Link to="/anuncios" className="nav-link" onClick={() => setMenuOpen(false)}>Anúncios</Link>
                <Link to="/motoristas" className="nav-link" onClick={() => setMenuOpen(false)}>Motoristas</Link>
            </nav>
        </header>
        
        {/* Botão de Chat Flutuante apenas no Mobile (escondido na própria página de conversas e se o chat modal estiver aberto) */}
        {isLogado && !location.pathname.startsWith('/minhas-conversas') && !isChatOpen && (
            <Link to="/minhas-conversas" className="chat-alerta-mobile-only" title="Minhas Conversas">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4.913 2.658c2.333-.292 4.7-.408 7.087-.408 2.387 0 4.754.116 7.087.408a2.18 2.18 0 0 1 1.913 2.168v10.32a2.18 2.18 0 0 1-1.913 2.168c-1.028.129-2.064.221-3.11.276l-2.756 2.756a.75.75 0 0 1-1.248-.548v-2.277a48.186 48.186 0 0 1-6.98-.276 2.18 2.18 0 0 1-1.913-2.168V4.826a2.18 2.18 0 0 1 1.913-2.168Z" />
                </svg>
                {conversasNaoLidas > 0 && (
                    <span className="badge-count">{conversasNaoLidas}</span>
                )}
            </Link>
        )}

            <ToastNotificacao 
                tipo={toastPagamento.tipo} 
                visivel={toastPagamento.visivel} 
                onFechar={() => {
                    const tipo = toastPagamento.tipo;
                    setToastPagamento({ visivel: false, tipo: 'sucesso' });
                    if (tipo === 'motorista') {
                        navigate('/perfil');
                    } else {
                        window.location.reload();
                    }
                }} 
            />

            {showPlanosModal && (
                <PlanosModal 
                    onClose={() => setShowPlanosModal(false)} 
                    onSelectPlano={(plano) => {
                        setShowPlanosModal(false);
                        setCheckoutPlano(plano);
                    }} 
                />
            )}
            
            {checkoutPlano && (
                <CheckoutModal 
                    plano={checkoutPlano} 
                    onClose={() => setCheckoutPlano(null)} 
                    onCheckout={handlePlanoSelecionado}
                />
            )}
        </>    
    )
}

export default Header
